import { memo, useEffect } from "react"

import toast from "@molecules/Toast/Toast"

import TopBar from "@organisms/TopBar/TopBar"

import { JobTimelineScheduler, JobTimelineViewSchedulerWrapperProps } from "./JobTimelineView.types"
import JobTimelineViewControls from "./JobTimelineViewControls/JobTimelineViewControls"
import useJobTimelineViewBryntumInstances from "./hooks/useJobTimelineViewBryntumInstances"
import useJobTimelineViewData from "./hooks/useJobTimelineViewData"
import useJobTimelineViewSchedulerConfig from "./hooks/useJobTimelineViewSchedulerConfig"
import useJobTimelineViewSchedulerRefresh from "./hooks/useJobTimelineViewSchedulerRefresh"
import useJobTimelineViewStates from "./hooks/useJobTimelineViewStates"
import useJobTimelineViewUIUtils from "./hooks/useJobTimelineViewUIUtils"

function JobTimelineViewSchedulerWrapper(props: JobTimelineViewSchedulerWrapperProps) {
    const { children } = props

    const {
        isSchedulerConfigured,
        setIsSchedulerConfigured,
        visibleDateRange,
        timelineViewOrientation,
        timeFrameType,
        hasVisibleDateRangeChanged,
        hasTimeFrameTypeChanged,
        shouldShowWeekends,
        hasToggledCancelledJobsChanged,
        hasToggledFinalizedJobsChanged,
    } = useJobTimelineViewStates()

    const { configureAndPopulateScheduler, dataIsLoaded } = useJobTimelineViewData()

    const { configureSchedulerTimezone, configureSchedulerDates } = useJobTimelineViewSchedulerConfig()

    const {
        calendarData,
        isFetchingCalendarData,
        isFetchingAvailabilitySchedulesData,
        isAvailabilitySchedulesError,
        isCalendarDataError,
        isUnscheduledJobsCountError,
    } = useJobTimelineViewData()

    const { startRefreshRowsInterval } = useJobTimelineViewSchedulerRefresh()

    const { schedulerPro } = useJobTimelineViewBryntumInstances()

    const { hideWeekendsFromScheduler, showWeekendsOnScheduler } = useJobTimelineViewUIUtils()

    useEffect(() => {
        const interval = startRefreshRowsInterval()

        return () => {
            clearInterval(interval)
        }
    }, [])

    useEffect(() => {
        if (!isSchedulerConfigured) {
            void configureSchedulerTimezone().then(() => {
                configureSchedulerDates()
                shouldShowWeekends ? showWeekendsOnScheduler() : hideWeekendsFromScheduler()
            })
        }
    }, [])

    useEffect(() => {
        if (
            (dataIsLoaded && !isSchedulerConfigured) ||
            (hasVisibleDateRangeChanged && dataIsLoaded) ||
            (hasToggledCancelledJobsChanged && !isFetchingCalendarData) ||
            (hasToggledFinalizedJobsChanged && !isFetchingCalendarData)
        ) {
            configureAndPopulateScheduler()

            if (!isSchedulerConfigured) {
                setIsSchedulerConfigured(true)
            }
        }
    }, [calendarData, isFetchingCalendarData, isFetchingAvailabilitySchedulesData])

    useEffect(() => {
        // this is a workaround to force vertical headers to rerender when the visible date changes
        // scheduler pro has a `visibleDate` property but to modify it causes too many side effects
        if (timelineViewOrientation === "vertical" && hasVisibleDateRangeChanged) {
            const schedulerInstance = schedulerPro.current?.instance as JobTimelineScheduler

            if (schedulerInstance) {
                schedulerInstance.currentVisibleDates = visibleDateRange

                schedulerInstance.refresh()
                schedulerInstance.resourceColumns.refresh()
            }
        }
    }, [visibleDateRange])

    useEffect(() => {
        if (timelineViewOrientation === "vertical" && hasTimeFrameTypeChanged) {
            const schedulerInstance = schedulerPro.current?.instance as JobTimelineScheduler

            if (schedulerInstance) {
                schedulerInstance.timeFrameType = timeFrameType
            }
        }
    }, [timeFrameType])

    useEffect(() => {
        if (isCalendarDataError) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not load jobs",
            })
        }
    }, [isCalendarDataError])

    useEffect(() => {
        if (isAvailabilitySchedulesError) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not load technicians",
            })
        }
    }, [isAvailabilitySchedulesError])

    useEffect(() => {
        if (isUnscheduledJobsCountError) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not load unscheduled jobs",
            })
        }
    }, [isUnscheduledJobsCountError])

    useEffect(() => {
        const schedulerInstance = schedulerPro.current?.instance as JobTimelineScheduler
        if (hasTimeFrameTypeChanged && schedulerInstance) {
            schedulerInstance.infiniteScroll = true
        }
    }, [timeFrameType])

    return (
        <>
            <TopBar.Portal>
                <JobTimelineViewControls />
            </TopBar.Portal>

            {children}
        </>
    )
}

export default memo(JobTimelineViewSchedulerWrapper)
